import { isPrimaryInsurance } from "./primaryItemScripts";
import { formatCurrency, getItemFromStorage, getItemFromStorageSimple, isDateInThePast } from "./script";
import { caseTypeOptions, frontendOptions, liabilityPolicyLimitsOptions, stateOfPIPsOptions, umPolicyLimitsOptions } from "./systemData";
import { detailedSearchOptions } from "../assets/data";

function resolvePolicyAmount(value, options = [], amountField = 'amount') {
    if (value == null || value === '') return 0
    const asNumber = Number(`${value}`.replace(/,/g, ''))
    if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
        return asNumber
    }
    const matchedOption = options.find((item) => item.value == value)
    return matchedOption?.[amountField] ?? matchedOption?.amount ?? 0
}

export function returnRepresentativeTypeFromEntity(entity) {
    switch (entity) {
        case `App\\Models\\CaseManager`:
            return 'case_managers'
        case 'App\\Models\\Attorney':
            return 'attorneys'
        default:
            return 'negotiators'
    }
}


export function returnOnlyPracticesThatAreOnTheCase(activeCase, practices) {
    if (activeCase && practices) {
        let finalItems = []
        practices.forEach(element => {
            activeCase.practices.forEach(item => {
                if (item.practice_id == element.value) {
                    finalItems.push(element)
                }
            })
        }); 

        return finalItems
    }

    return null
}


export function changeWordingForWC(activeCase, text, textChange) {
    let founditem = caseTypeOptions()?.find(item => item.value == activeCase?.case?.case_type_id) 
    if (founditem) {
        if (founditem.category == 'WC') {
            return textChange
        }
    }

    return text
}

export function getPIPStatesDescription(state) {
    let states = stateOfPIPsOptions()
    if (state && states) {
        
        let matchedState = states.find(item => item.label == state)

        if (matchedState && matchedState.description) {
            return matchedState.description
        }
    }

    return null
}

export function getUMReducedAmount(primaryLiability, umItem) {
    if (primaryLiability && umItem) {  
        const liabilityAmount = resolvePolicyAmount(primaryLiability.policy_limit_patient, liabilityPolicyLimitsOptions() || [], 'amount_patient')
        const umAmount = resolvePolicyAmount(umItem.um_policy_limit_patient, umPolicyLimitsOptions() || [], 'amount_patient')
        if (liabilityAmount && umAmount) {
            let difference = umAmount - liabilityAmount
            if (difference <= 0) {
                return 0
            }
            return formatCurrency(difference)
        }
    }  else {
        const umAmount = resolvePolicyAmount(umItem?.um_policy_limit_patient, umPolicyLimitsOptions() || [], 'amount_patient')
        if (umAmount) {
            return formatCurrency(umAmount)
        }
    }

    return 0
}

export function getUMReducedAmountClean(primaryLiability, umItem) {
    if (primaryLiability && umItem) {  
        const liabilityAmount = resolvePolicyAmount(primaryLiability.policy_limit_patient, liabilityPolicyLimitsOptions() || [], 'amount_patient')
        const umAmount = resolvePolicyAmount(umItem.um_policy_limit_patient, umPolicyLimitsOptions() || [], 'amount_patient')
        if (liabilityAmount && umAmount) {
            let difference = umAmount - liabilityAmount
            if (difference <= 0) {
                return 0
            }
            return difference
        }
    }

    return 0
}


export function isReducedUMZeroByLiability(primaryLiability, umItem) {
    if (!primaryLiability || !umItem) return false
    const liabilityAmount = resolvePolicyAmount(primaryLiability.policy_limit_patient, liabilityPolicyLimitsOptions() || [], 'amount_patient')
    const umAmount = resolvePolicyAmount(umItem.um_policy_limit_patient, umPolicyLimitsOptions() || [], 'amount_patient')
    // Reduced UM availability is only $0.00 when both limits are known and liability meets/exceeds the UM limit
    return umAmount > 0 && liabilityAmount > 0 && (umAmount - liabilityAmount) <= 0
}

export function hasPositiveUMAvailable(primaryLiability, umItem) {
    if (!umItem || Number(umItem.has_um) !== 1) return false

    const umAmount = resolvePolicyAmount(umItem.um_policy_limit_patient, umPolicyLimitsOptions() || [], 'amount_patient')
    if (umAmount <= 0) return false

    if (Number(umItem.um_type) === 0) {
        return true
    }

    if (Number(umItem.um_type) === 1) {
        const liabilityAmount = resolvePolicyAmount(primaryLiability?.policy_limit_patient, liabilityPolicyLimitsOptions() || [], 'amount_patient')
        return liabilityAmount > 0 ? umAmount - liabilityAmount > 0 : true
    }

    return false
}

export function getUMReducedAmountCleanV2(primaryLiability, umItem) {
    if (primaryLiability && umItem) {  
        const liabilityAmount = resolvePolicyAmount(primaryLiability.policy_limit_patient, liabilityPolicyLimitsOptions() || [], 'amount_patient')
        const umAmount = resolvePolicyAmount(umItem.um_policy_limit_patient, umPolicyLimitsOptions() || [], 'amount_patient')
        if (liabilityAmount && umAmount) {
            return liabilityAmount + umAmount
        } else if (umAmount) {
            return umAmount
        } else if (liabilityAmount) {
            return liabilityAmount
        }
    }

    return 0
}

export function getDisplayNameForLien(type) {
    if (type) {
        switch (type) {
            case 'intent':
                return 'Intent of Lien'
            case 'lien':
                return 'Lien' 
            case 'amend':
                return 'Amended Lien' 
            case 'satisfy':
                return 'Satisfaction'
            default:
                return null;
        }
    }

    return null
}

/** Keep certified mail / tracking IDs as plain digit strings (never scientific notation). */
export function formatCertifiedMailNumber(value) {
    if (value == null || value === '') {
        return ''
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return value.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 0 })
    }

    const str = String(value).trim()
    if (!str) {
        return ''
    }

    // Expand strings like "9.41E+21" (e.g. from Excel) into a plain digit string when possible.
    const sciMatch = str.match(/^([+-]?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/)
    if (sciMatch) {
        const sign = sciMatch[1] === '-' ? '-' : ''
        const intPart = sciMatch[2]
        const fracPart = sciMatch[3] || ''
        const exp = parseInt(sciMatch[4], 10)
        const digits = intPart + fracPart
        const pointIndex = intPart.length + exp

        if (pointIndex <= 0) {
            return `${sign}0.${'0'.repeat(-pointIndex)}${digits}`
        }
        if (pointIndex >= digits.length) {
            return `${sign}${digits}${'0'.repeat(pointIndex - digits.length)}`
        }
        return `${sign}${digits.slice(0, pointIndex)}.${digits.slice(pointIndex)}`
    }

    return str
}

export function getDisplayNameForLienWithCounty(type, countyName) {
    const baseLabel = getDisplayNameForLien(type)
    const county = `${countyName || ''}`.trim()

    if (!baseLabel) {
        return null
    }

    if (!county) {
        return baseLabel
    }

    return `${baseLabel} - ${county}`
}

export function showMajorMedOnPrint(data) {
    if (data) {

        if (!data.no_coverage && !data.no_insurance  ) { // && !data.cancel_date && !data.end_date
            if (data.cancel_date && isDateInThePast(data.cancel_date)) {
                return false
            }

            if (data.end_date && isDateInThePast(data.end_date)) {
                return false
            }
            
            return true
        }
    }

    return false
}


export function searchTabInCaseType(activeCase, type) {
    if (activeCase) {
        let foundItem = caseTypeOptions()?.find(item => item.value == activeCase.case.case_type_id)

        if (foundItem && foundItem.tabs) {
            let tab = foundItem.tabs.find(item => item == type)

            if (tab) {
                return true
            }
        }
    }
    
    return false
}

/** Config value for "Surgery Recommended" in frontend_options.surgeryRecomandation */
export function getSurgeryRecommendedStatusValue() {
    const opts = frontendOptions()?.surgeryRecomandation || []
    const match = opts.find((o) => {
        const raw = String(o.label || '').trim().toLowerCase().replace(/\s+/g, ' ')
        return raw === 'surgery recomended' || raw === 'surgery recommended'
    })
    if (match) {
        return match.value
    }
    const fallback = opts.find((o) => o.value == 1 || o.value === '1')
    return fallback != null ? fallback.value : 1
}

/** Matches backend SurgeryReferralMatcher::isSurgeryEligibleDescription */
export function isSurgeryEligibleServiceDescription(description) {
    if (!description) {
        return false
    }
    const lower = String(description).toLowerCase()
    return lower.includes('surgery') || lower.includes('surgical') || lower.includes('*mua')
}

/** Non-DIAG surgery-eligible service with no linked case_surgery row */
export function serviceNeedsSurgeryLink(service, surgeries) {
    if (!service || service.class === 'DIAG') {
        return false
    }
    if (!isSurgeryEligibleServiceDescription(service.description)) {
        return false
    }
    const list = Array.isArray(surgeries) ? surgeries : []
    return !list.some((surgery) => surgery?.case_service_id === service.id)
}

/** Surgery with no linked case_services row can be hard-deleted (e.g. orphaned eCM1 data). */
export function surgeryCanBeDeleted(surgery) {
    if (!surgery) {
        return false
    }
    if (surgery.can_delete === false) {
        return false
    }
    return surgery.case_service_id == null || surgery.case_service_id === ''
}

export function isCaseTypeWellness(activeCase) {
    if (activeCase) {
        let foundItem = caseTypeOptions()?.find(item => item.value == activeCase.case.case_type_id)
        if (foundItem && foundItem.category == 'WELL') {
            return true
        }
    }
    return false
}

export function isMPClaim(activeCase) {
    if (activeCase) {
        let primary = isPrimaryInsurance(activeCase, 'mp_pip_um')
        if (primary && primary.has_mp == 1 && primary.mp_claim_number) {
            return true
        }
    }

    return false
}

export function retriveWorkQNameFromID(workQID) {
    if (workQID) {
        const systemSettings = getItemFromStorage('settings')  

        if (systemSettings && systemSettings.work_queues) {
            let foundWorkQ = systemSettings.work_queues.find(item => item.id == workQID)

            return foundWorkQ?.name
        }
    }

    return null
}

export function retrieveWorkQSlugFromId(workQID) {
    if (workQID == null || workQID === '' || workQID === 0) {
        return null
    }
    const systemSettings =
        typeof window !== 'undefined' && window.systemSettings
            ? window.systemSettings
            : getItemFromStorageSimple('settings')

    const standard = systemSettings?.work_queues ?? []
    const foundWorkQ = standard.find(
        (item) => item != null && String(item.id) === String(workQID),
    )
    return foundWorkQ?.slug ?? null
}

export function checkCurentStageForSurgery(stage) { // fallback returns first drawer
    if (stage) {
        switch (stage) {
            case 1:
                return 1
            case 2:
                return 1
            case 3:
                return 2
            case 4:
                return 2
            case 5:
                return 2
            case 6:
                return 3
            case 7:
                return 3
            default:
                return 1
        }
    }

    return 1
}

export function determinTransferStatus(transfers) {
    if (transfers && transfers.length) {
        let findPending = transfers.find(item => item.status == 'Pending')
        if (findPending) {
            return 'pending'
        } 

        let findReview = transfers.find(item => item.status == 'Review')
        if (findReview) {
            return 'review'
        } 
    }
    return null
}

export const miscellaneous = {
    areValuesDifferent: (value1, value2) => {
        if (value1 && value2) {
            return value1 !== value2
        }
        return false
    },
    arraysHaveSameItems: (arr1, arr2) => {
        if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
        if (arr1.length !== arr2.length) return false;
        // Create frequency maps for both arrays
        const map1 = {};
        const map2 = {};
        for (let item of arr1) {
            map1[item] = (map1[item] || 0) + 1;
        }
        for (let item of arr2) {
            map2[item] = (map2[item] || 0) + 1;
        }
        // Compare the frequency maps
        for (let key in map1) {
            if (map1[key] !== map2[key]) return false;
        }
        return true;
    },
    numberOfObjectValues: (obj, exclude) => {
        if (obj) {
            let copyObj = JSON.parse(JSON.stringify(obj))
            if (exclude) {
                delete copyObj[exclude]
            }
    
            if (copyObj.location) {
                const userLocations = getItemFromStorageSimple('userLocations')
                if (userLocations) {
                    if (miscellaneous.arraysHaveSameItems(copyObj.location, userLocations)) {
                        delete copyObj.location
                    }
                }
            }
            return Object.values(copyObj).filter(item => item).length
        }
        
        return null
    },
    isNumber: (value) => {
        return typeof value === 'number' && !isNaN(value);
    },
    isObject: (value) => {
        return typeof value === 'object' && value !== null && !Array.isArray(value)
    },
    convert24to12: (time24) => {
        // Expects time24 as "HH:mm" or "HH:mm:ss"
        if (typeof time24 !== 'string') return '';
        let [hours, minutes, seconds] = time24.split(':');
        hours = parseInt(hours, 10);
        if (isNaN(hours) || isNaN(parseInt(minutes, 10))) return '';
        let period = hours >= 12 ? 'PM' : 'AM';
        let hour12 = hours % 12 || 12; // Convert 0 to 12
        let result = hour12 + ':' + minutes.padStart(2, '0');
        // if (seconds !== undefined) {
        //     result += ':' + seconds.padStart(2, '0');
        // }
        return result + ' ' + period;
    },
    simplePercentage: (totalAmount, amount) => {
        if (totalAmount && amount) {
            return ((amount*100)/totalAmount).toFixed(2)
        }
        return null
    },
    setCaseAlertsLocalStorage: (caseId) => {
        if (caseId) {
            const expireTime = new Date().getTime() + 24 * 60 * 60 * 1000; // 1 day from now in ms
            try {
                let caseAlertsArr = [];
                const existing = localStorage.getItem('caseAlerts');
                if (existing) {
                    try {
                        caseAlertsArr = JSON.parse(existing) || [];
                        if (!Array.isArray(caseAlertsArr)) {
                            caseAlertsArr = [];
                        }
                    } catch (eJSON) {
                        caseAlertsArr = [];
                    }
                }
                // Remove old alert for this caseId, if present
                caseAlertsArr = caseAlertsArr.filter(item => item.caseId != caseId);
                // Add new one
                caseAlertsArr.push({
                    caseId: caseId,
                    expires: expireTime
                });
                localStorage.setItem('caseAlerts', JSON.stringify(caseAlertsArr));
            } catch (e) {
                // Fail silently
            }
        }
    },
    getCaseAlertsLocalStorage: (caseId) => {
        try {
            const caseAlerts = localStorage.getItem('caseAlerts');
            if (caseAlerts) {
                let alertsArr = JSON.parse(caseAlerts);
                if (Array.isArray(alertsArr) && caseId !== undefined && caseId !== null) {
                    let modified = false;
                    // Remove any expired items from the array (not just queried one)
                    const now = Date.now();
                    alertsArr = alertsArr.filter(item => {
                        // Only keep non-expired ones (keep if it has expires and is in future or no expires at all)
                        if (!item.expires || now > item.expires) {
                            modified = true;
                            return false; // this one is expired, remove it
                        }
                        return true; // not expired, keep
                    });
                    if (modified) {
                        localStorage.setItem('caseAlerts', JSON.stringify(alertsArr));
                    }
                    // Now check if an alert for caseId is present
                    const alert = alertsArr.find(item => item.caseId == caseId) || null;
                    return alert;
                }
                return alertsArr;
            }
            return null;
        } catch (e) {
            return null;
        }
    }
}


export function generateRandomString(length) {
    return Math.random().toString(36).substring(2, length + 2);
    // var result           = '';
    // var characters       = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    // var charactersLength = characters.length;
    // for ( var i = 0; i < length; i++ ) {
    //     result += characters.charAt(Math.floor(Math.random() * charactersLength));
    // }
    // return result;
}

export function compareSpecialityValues(kept, missed) {

    if ((kept && missed) || (!kept && missed)) {
        return missed > kept
    }
    return false
}


export function searchGetTypeOfValue(value, compoundSearchDirect = []) {
    // if (typeof value !== 'string') return 'unknown';
    const v = `${value ?? ''}`.trim();
    const onlyLetters = /^[A-Za-z]+$/;
    const onlyNumbers = /^\d+$/;
    // Chart numbers: 1+ leading UPPERCASE letters, then only digits (no spaces/symbols).
    // Require at least 3 digits to avoid treating short codes like "mva1" as chart numbers.
    // Examples: A1212121, S1322435, AAA121121
    const chartNumberPattern = /^[A-Z]+[0-9]{4,}$/;
    // Common date formats: yyyy-mm-dd, mm/dd/yyyy, dd-mm-yyyy
    const datePatterns = [
        /^\d{4}-\d{1,2}-\d{1,2}$/,           // 2020-1-1 or 2020-12-31
        /^\d{1,2}\/\d{1,2}\/\d{4}$/,         // 1/1/2020 or 12/31/2020
        /^\d{1,2}-\d{1,2}-\d{4}$/,           // 1-1-2020 or 31-12-2020
        /^\d{4}\/\d{1,2}\/\d{1,2}$/,         // 2020/1/1 or 2020/12/31
        /^\d{1,2}\.\d{1,2}\.\d{4}$/          // 1.1.2020 or 31.12.2020
    ];

    if (onlyLetters.test(v)) {
        if (compoundSearchDirect.length == 0) {
            return [detailedSearchOptions.find(item => item.apiProp == 'first_name'), detailedSearchOptions.find(item => item.apiProp == 'last_name')];
        } else {
            return compoundSearchDirect.filter(item => item.apiProp == 'first_name' || item.apiProp == 'last_name');
        }
        // return [detailedSearchOptions.find(item => item.apiProp == 'first_name'), detailedSearchOptions.find(item => item.apiProp == 'last_name')];
    }
    if (onlyNumbers.test(v)) {
        // return [detailedSearchOptions.find(item => item.apiProp == 'chart_number')];

        // If the number starts with 19, 20, 21, ..., 40 (e.g., years), include 'chart_number' as well
        const startsWithChartNumberYear = /^(1[9]|2[0-9]|3[0-9]|40)\d*/.test(v);
        if (startsWithChartNumberYear) {
            // Add chart_number alongside ng_number
            return [
                detailedSearchOptions.find(item => item.apiProp == 'ng_number'),
                detailedSearchOptions.find(item => item.apiProp == 'chart_number')
            ].filter(Boolean); // filter(Boolean) in case either is undefined
        } 
        return [detailedSearchOptions.find(item => item.apiProp == 'ng_number')];
    
    }
    if (datePatterns.some(pattern => pattern.test(v))) {
        if (compoundSearchDirect.length == 0) {
            return [detailedSearchOptions.find(item => item.apiProp == 'incident_date'), detailedSearchOptions.find(item => item.apiProp == 'dob')];
        } else {
            return compoundSearchDirect.filter(item => item.apiProp == 'incident_date' || item.apiProp == 'dob');
        }
    }
    // Chart numbers: start with exactly 1 letter, then only digits
    if (chartNumberPattern.test(v)) {
        return [detailedSearchOptions.find(item => item.apiProp == 'chart_number')].filter(Boolean);
    }
    return null;
}


export function getInitialsColor(initials) {
    if (!initials) {
        return 'hsl(0, 0%, 50%)';
    }
    const str = initials.toUpperCase().trim();
   
    const first = str.charCodeAt(0) || 0;
    const second = str.charCodeAt(1) || 0;
   
    const hue = (first * 31 + second * 17) % 360;
   
    return `hsl(${hue}, 65%, 50%)`;
}

export function groupByState(items) {
    return items.reduce((acc, item) => {
      const state = item.state;
      if (!acc[state]) {
        acc[state] = [];
      }
      acc[state].push({
        label: item.name,
        value: item.id,
        ...item
      });
      return acc;
    }, {});
}